import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Head } from "vite-react-ssg";
import { MDXProvider } from "@mdx-js/react";
import { ArrowLeft, Clock } from "lucide-react";
import BackgroundGradient from "@/components/landing/BackgroundGradient";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { AuthorBlock } from "@/components/blog/AuthorBlock";
import { RelatedArticles } from "@/components/blog/RelatedArticles";
import { BlogCTA } from "@/components/blog/BlogCTA";
import { FAQAccordion } from "@/components/blog/mdx/FAQAccordion";
import { mdxComponents } from "@/components/blog/mdx";
import { getArticleBySlug } from "@/lib/blog";

const SITE_URL = "https://www.dyna-agency.com";

const AUTHOR_NAME = {
  pinch: "Pinch Guigui",
  ezra: "Ezra Rothchild",
} as const;

const AUTHOR_SAMEAS: Record<"pinch" | "ezra", string[] | undefined> = {
  pinch: ["https://www.linkedin.com/in/pinchas-guigui-63b58a247"],
  ezra: undefined,
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const article = useMemo(() => (slug ? getArticleBySlug(slug) : null), [slug]);
  const dir = i18n.language === "he" ? "rtl" : "ltr";

  if (!article) {
    return (
      <>
        <BackgroundGradient />
        <div style={{ position: "relative", zIndex: 1, minHeight: "100vh", color: "var(--dyna-fg)" }}>
          <Navbar />
          <main style={{ padding: "160px 24px 80px", maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
            <h1
              style={{
                fontFamily: "\"Coolvetica\", system-ui, sans-serif",
                fontSize: "clamp(28px, 4vw, 40px)",
                textTransform: "uppercase",
                color: "var(--dyna-fg)",
                margin: "0 0 16px",
              }}
            >
              {t("pages.blog.notFound")}
            </h1>
            <Link
              to="/blog"
              style={{
                color: "var(--lavender-300)",
                textDecoration: "underline",
                fontFamily: "Geist, system-ui, sans-serif",
              }}
            >
              {t("pages.blog.backToIndex")}
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const fm = article.frontmatter;
  const authorName = AUTHOR_NAME[fm.author];
  const authorSameAs = AUTHOR_SAMEAS[fm.author];
  const url = `${SITE_URL}/blog/${fm.slug}`;
  const coverUrl = fm.coverImage
    ? fm.coverImage.startsWith("http")
      ? fm.coverImage
      : `${SITE_URL}${fm.coverImage}`
    : `${SITE_URL}/og-image.png`;
  const dateLabel = new Date(fm.publishedAt).toLocaleDateString(
    i18n.language === "he" ? "he-IL" : "en-US",
    { year: "numeric", month: "long", day: "numeric" },
  );

  const articleSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: fm.title,
    description: fm.excerpt,
    image: coverUrl,
    datePublished: fm.publishedAt,
    dateModified: fm.updatedAt || fm.publishedAt,
    author: {
      "@type": "Person",
      name: authorName,
      ...(authorSameAs && { sameAs: authorSameAs }),
    },
    publisher: {
      "@type": "Organization",
      name: "Dyna Agency",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-192.png` },
    },
    mainEntityOfPage: url,
    inLanguage: fm.language,
  };

  const faqSchema =
    fm.faqs && fm.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: fm.faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }
      : null;

  const ArticleComponent = article.Component;

  return (
    <>
      <SEO
        path={`/blog/${fm.slug}`}
        title={fm.metaTitle || fm.title}
        description={fm.metaDescription || fm.excerpt}
        image={fm.coverImage}
      />
      <Head>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Head>
      <BackgroundGradient />
      <div dir={dir} style={{ position: "relative", zIndex: 1, minHeight: "100vh", color: "var(--dyna-fg)" }}>
        <Navbar activePage="blog" />
        <main style={{ padding: "120px clamp(20px, 5vw, 80px) 80px", maxWidth: 1080, margin: "0 auto" }}>
          <Link
            to="/blog"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "var(--lavender-400)",
              textDecoration: "none",
              marginBottom: "var(--s-md)",
            }}
          >
            <ArrowLeft size={14} />
            {t("pages.blog.backToIndex")}
          </Link>

          <header style={{ maxWidth: 720, margin: "0 auto var(--s-md)" }}>
            {fm.tags.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {fm.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                      color: "var(--lavender-400)",
                      background: "rgba(156,139,194,0.10)",
                      padding: "3px 10px",
                      borderRadius: 999,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <h1
              style={{
                fontFamily: "\"Coolvetica\", system-ui, sans-serif",
                fontSize: "clamp(32px, 5vw, 56px)",
                lineHeight: 1.05,
                textTransform: "uppercase",
                letterSpacing: "0.005em",
                color: "var(--dyna-fg)",
                margin: "0 0 16px",
              }}
            >
              {fm.title}
            </h1>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: "var(--dyna-fg-muted)",
                flexWrap: "wrap",
              }}
            >
              <span>{authorName}</span>
              <span>·</span>
              <span>{dateLabel}</span>
              <span>·</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Clock size={11} />
                {t("pages.blog.readingTime", { minutes: article.readingTime })}
              </span>
            </div>
          </header>

          {fm.coverImage && (
            <img
              src={fm.coverImage}
              alt={fm.title}
              style={{
                display: "block",
                maxWidth: 1080,
                width: "100%",
                margin: "0 auto var(--s-lg)",
                borderRadius: 16,
                border: "1px solid var(--dyna-border)",
              }}
            />
          )}

          <article className="dyna-prose">
            <MDXProvider components={mdxComponents}>
              <ArticleComponent />
            </MDXProvider>
          </article>

          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <AuthorBlock author={fm.author} />
            <BlogCTA type="calendly" />
            <RelatedArticles currentSlug={fm.slug} />
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
